/**
 * Gift With Purchase (GWP) utilities
 *
 * Client-safe: parsing persisted state, saving selections via BFF fetch,
 * and eligibility checks. Server-only mutations live in BFF route files.
 *
 * Quantity for gift lines is owned by OrderApp (always 1). The storefront
 * never sets gift quantity — it selects/swaps via checkoutGwpUpdate and
 * clears the selection when the bag changes (legacy gwpProductCheck).
 */

import { devError } from '@/lib/utils/devLog';
import { checkoutActions, checkoutState } from '@/states/checkout';

export const GWP_DO_NOT_NEED = 'doNotNeedGift' as const;

export interface GwpProduct {
  slug: string;
  id: string;
  name: string;
  available_quantity: number;
  product_type_slug: string;
  threshhold: string;
  thumbnail_alt: string | null;
  thumbnail_url: string | null;
}

interface CheckoutGwpInput {
  doNotNeedGift: boolean;
  gwpProductId: string;
  lineIdToRemove: string;
}

interface GwpError {
  field: string | null;
  code: string | null;
  message: string | null;
}

/**
 * Parse the persisted gwp.product metafield from checkout metadata.
 *
 * Values:
 * - null → no choice yet → ''
 * - "false" → user declined → GWP_DO_NOT_NEED
 * - JSON { id, ... } → selected gift → product id
 */
export function parseGwpMetafield(raw: string | null | undefined): string {
  if (!raw) return '';
  if (raw.toLowerCase() === 'false') return GWP_DO_NOT_NEED;
  try {
    const parsed = JSON.parse(raw);
    return parsed?.id ?? '';
  } catch {
    return '';
  }
}

/**
 * Check if a gift product is eligible based on cart total and threshold.
 */
export function isGiftAvailable(
  product: GwpProduct,
  cartTotal: number,
  registerMyWatch: boolean
): boolean {
  return Number(product.threshhold) <= cartTotal && registerMyWatch;
}

interface GiftLineCandidate {
  readonly id: string;
  /** Local optimistic line product type (CheckoutLine.productType) */
  readonly productType?: string | null;
  readonly variant?: {
    readonly product?: {
      readonly id?: string | null;
      readonly productType?: { readonly slug?: string | null } | null;
    } | null;
  } | null;
}

/**
 * Whether a product-type slug is a complimentary gift / GWP line.
 */
export function isGiftProductType(slug?: string | null): boolean {
  return slug?.toLowerCase() === 'gift';
}

function isGiftLine(line: GiftLineCandidate): boolean {
  return (
    isGiftProductType(line.productType) ||
    isGiftProductType(line.variant?.product?.productType?.slug)
  );
}

/**
 * Find every gift line ID on the checkout (duplicates can exist if GWP was applied twice).
 */
export function findGiftLineIds(lines: readonly GiftLineCandidate[]): string[] {
  return lines.filter(isGiftLine).map((line) => line.id);
}

/**
 * Find the existing gift line ID in checkout lines.
 */
export function findGiftLineId(lines: readonly GiftLineCandidate[]): string {
  return findGiftLineIds(lines)[0] ?? '';
}

/**
 * Whether the checkout already reflects the requested GWP selection with no duplicates.
 * Used to skip remove-and-re-add cycles on payment retry (BEC-24647).
 */
export function isGwpSelectionAlreadyApplied(
  selection: string,
  lines: readonly GiftLineCandidate[]
): boolean {
  const giftLineIds = findGiftLineIds(lines);
  if (giftLineIds.length > 1) return false;

  if (selection === '' || selection === GWP_DO_NOT_NEED) {
    return giftLineIds.length === 0;
  }

  if (giftLineIds.length === 0) return false;

  return findGiftProductId(lines) === selection;
}

/** Serializes GWP mutations so concurrent pay clicks cannot stack gift lines. */
let gwpApplyChain: Promise<unknown> = Promise.resolve();

async function withGwpApplyLock<T>(action: () => Promise<T>): Promise<T> {
  const run = gwpApplyChain.then(action, action);
  gwpApplyChain = run.then(
    () => undefined,
    () => undefined
  );
  return run;
}

/** @internal Resets the GWP apply mutex between tests. */
export function resetGwpApplyLockForTests(): void {
  gwpApplyChain = Promise.resolve();
}

/**
 * Saleor product id for the complimentary gift line (used to re-select GWP UI).
 */
export function findGiftProductId(lines: readonly GiftLineCandidate[]): string {
  const giftLine = lines.find(isGiftLine);
  return giftLine?.variant?.product?.id ?? '';
}

interface GwpMutationStep {
  doNotNeedGift: boolean;
  gwpProductId: string;
  lineIdToRemove: string;
}

/** OrderApp express-checkout GWP payload (no doNotNeedGift flag). */
export interface OrderGwpMutationStep {
  gwpProductId: string;
  lineIdToRemove: string;
}

/**
 * Builds checkout GWP mutation steps, or null when the selection is already applied.
 */
export function buildGwpMutationSteps(
  selection: string,
  lines: readonly GiftLineCandidate[]
): GwpMutationStep[] | null {
  if (isGwpSelectionAlreadyApplied(selection, lines)) {
    return null;
  }

  const existingGiftLineIds = findGiftLineIds(lines);
  const isDeclined = selection === '' || selection === GWP_DO_NOT_NEED;

  if (isDeclined) {
    if (existingGiftLineIds.length === 0) {
      return [{ doNotNeedGift: true, gwpProductId: '', lineIdToRemove: '' }];
    }

    const steps: GwpMutationStep[] = existingGiftLineIds.slice(0, -1).map((lineIdToRemove) => ({
      doNotNeedGift: false,
      gwpProductId: '',
      lineIdToRemove,
    }));

    steps.push({
      doNotNeedGift: true,
      gwpProductId: '',
      lineIdToRemove: existingGiftLineIds.at(-1) ?? '',
    });

    return steps;
  }

  const steps: GwpMutationStep[] = existingGiftLineIds.map((lineIdToRemove) => ({
    doNotNeedGift: false,
    gwpProductId: '',
    lineIdToRemove,
  }));

  steps.push({
    doNotNeedGift: false,
    gwpProductId: selection,
    lineIdToRemove: '',
  });

  return steps;
}

/**
 * Builds express order GWP mutation steps, or null when the selection is already applied.
 */
export function buildOrderGwpMutationSteps(
  selection: string,
  lines: readonly GiftLineCandidate[]
): OrderGwpMutationStep[] | null {
  const checkoutSteps = buildGwpMutationSteps(selection, lines);
  if (!checkoutSteps) return null;

  return checkoutSteps.map((step) => ({
    gwpProductId: step.gwpProductId,
    lineIdToRemove: step.lineIdToRemove,
  }));
}

/**
 * Apply a GWP selection without stacking.
 * Clears every existing gift line first, then writes the new selection.
 * Required on payment retry — local checkout lines are often stale after the
 * previous attempt already added a gift.
 */
export async function applyCheckoutGwpSelection(
  checkoutId: string,
  selection: string,
  lines: readonly GiftLineCandidate[]
): Promise<GwpError[] | null> {
  return withGwpApplyLock(async () => {
    const steps = buildGwpMutationSteps(selection, lines);
    if (!steps) return null;

    for (const checkoutGwp of steps) {
      const errors = await checkoutGwpUpdate(checkoutId, {
        doNotNeedGift: checkoutGwp.doNotNeedGift,
        gwpProductId: checkoutGwp.gwpProductId,
        lineIdToRemove: checkoutGwp.lineIdToRemove,
      });
      if (errors?.length) return errors;
    }

    return null;
  });
}

/**
 * Resolve the active GWP radio value from local state, metafield, or gift line.
 * Used after payment failure / remount when the gift remains on checkout.
 */
export function resolveActiveGwpSelection(input: {
  gwpProductId?: string | null;
  gwpProductMetafield?: string | null;
  lines?: readonly GiftLineCandidate[];
}): string {
  const fromMetafield = parseGwpMetafield(input.gwpProductMetafield);
  if (fromMetafield) return fromMetafield;

  const local = input.gwpProductId;
  if (local === GWP_DO_NOT_NEED) return GWP_DO_NOT_NEED;
  if (local) {
    const parsedLocal = parseGwpMetafield(local);
    if (parsedLocal) return parsedLocal;
    // Valtio may store a bare product id (not metafield JSON / "false")
    if (local.toLowerCase() !== 'false' && !local.trimStart().startsWith('{')) {
      return local;
    }
  }

  return findGiftProductId(input.lines ?? []);
}

/**
 * Whether a GWP selection (or gift line) is present and should be cleared
 * when the shopping bag changes — mirrors legacy `gwpProduct` getter truthiness.
 */
export function hasGwpSelection(input: {
  gwpProductId?: string | null;
  gwpProductMetafield?: string | null;
  lines?: readonly GiftLineCandidate[];
}): boolean {
  if (resolveActiveGwpSelection(input)) return true;
  return findGiftLineIds(input.lines ?? []).length > 0;
}

/**
 * Legacy `gwpProductCheck`: if a gift was selected (or a gift line exists),
 * clear it via OrderApp before cart line add/remove so gifts cannot stack.
 * Clears every gift line when duplicates already exist.
 */
export async function gwpProductCheck(): Promise<void> {
  const checkoutId = checkoutState.id;
  if (!checkoutId) return;

  const checkoutLines = checkoutState.checkout?.lines ?? [];
  const mergedLines = [...checkoutLines, ...(checkoutState.lines ?? [])];
  const linesForGwp = [...new Map(mergedLines.map((line) => [line.id, line])).values()];

  if (
    !hasGwpSelection({
      gwpProductId: checkoutState.gwpProductId,
      lines: linesForGwp,
    })
  ) {
    return;
  }

  await applyCheckoutGwpSelection(checkoutId, '', linesForGwp);
  checkoutActions.setGwpProduct(null);
}

/**
 * Save GWP selection for a standard checkout.
 *
 * Routes through the BFF (/api/checkout/gwp/update) so the server-side
 * app token authenticates the OrderApp mutation — the client can't access
 * the encrypted httpOnly auth cookie directly.
 */
export async function checkoutGwpUpdate(
  checkoutId: string,
  input: CheckoutGwpInput
): Promise<GwpError[] | null> {
  try {
    const response = await fetch('/api/checkout/gwp/update', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ checkoutId, checkoutGwp: input }),
    });

    const data = await response.json();

    if (!response.ok) {
      return [
        {
          field: null,
          code: 'SERVER_ERROR',
          message: data.message ?? 'Failed to save gift selection',
        },
      ];
    }

    return null;
  } catch (error) {
    devError('[GWP] checkoutGwpUpdate failed:', error);
    return [{ field: null, code: 'UNKNOWN', message: 'Failed to save gift selection' }];
  }
}
